import { defineComponent, onMounted, ref, watch } from 'vue'
import { getPersonList } from '../manage/api'
import {
  deleteCommentTemplate,
  getCommentTemplateDetail,
  getCommentTemplateList,
  saveCommentTemplate
} from './api'

const EMPTY_FORM = {
  id: '',
  content: '',
  scope: 'global',
  category: '',
  sort_order: 0,
  state: 1
}

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  if (payload && Array.isArray(payload.list)) return payload.list
  if (payload && Array.isArray(payload.items)) return payload.items
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 10, total_pages: 1 }
  }
  const total = Number(payload.total || 0)
  const page = Number(payload.page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 10)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 10,
    total_pages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1
  }
}

function normalizeDetail(res) {
  const payload = unwrapPayload(res)
  if (payload && payload.template && typeof payload.template === 'object') {
    return payload.template
  }
  if (payload && payload.detail && typeof payload.detail === 'object') {
    return payload.detail
  }
  return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
}

function pickScopeLabel(scope) {
  if (scope === 'global') return '全局'
  if (scope === 'person') return '个人'
  return scope || '-'
}

function pickStateLabel(state) {
  if (state === 1 || state === '1') return '有效'
  if (state === 0 || state === '0') return '已删'
  return '-'
}

export default defineComponent({
  name: 'CommentTemplateView',
  setup() {
    const templateList = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const detailLoading = ref(false)

    const currentPage = ref(1)
    const pageSize = ref(10)
    const totalPages = ref(1)
    const total = ref(0)

    const filterScope = ref('')
    const filterCategory = ref('')
    const filterState = ref('1')
    const filterPersonId = ref('')
    const keyword = ref('')

    const modalVisible = ref(false)
    const modalTitle = ref('新增模板')
    const form = ref({ ...EMPTY_FORM })

    const selectedPerson = ref(null)
    const personSearchQuery = ref('')
    const personSearchResults = ref([])
    const personSearchLoading = ref(false)
    const personDropdownOpen = ref(false)

    let personSearchTimer = null
    let listSearchTimer = null

    const buildListParams = () => {
      const params = {
        page: currentPage.value,
        page_size: pageSize.value
      }
      if (filterScope.value) params.scope = filterScope.value
      if (filterCategory.value.trim()) params.category = filterCategory.value.trim()
      if (filterState.value !== '') params.state = Number(filterState.value)
      const personId = String(filterPersonId.value || '').trim()
      if (personId) params.person_id = Number(personId)
      const q = String(keyword.value || '').trim()
      if (q) params.q = q
      return params
    }

    const fetchTemplateList = async () => {
      loading.value = true
      try {
        const response = await getCommentTemplateList(buildListParams())
        templateList.value = extractList(response)
        const meta = extractMeta(response)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = meta.total_pages
        total.value = meta.total
      } catch (error) {
        console.error('获取评论模板失败:', error)
        templateList.value = []
        alert(error.message || '获取评论模板失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const searchPersons = async () => {
      const searchKeyword = String(personSearchQuery.value || '').trim()
      if (!searchKeyword) {
        personSearchResults.value = []
        return
      }

      personSearchLoading.value = true
      try {
        const response = await getPersonList({
          page: 1,
          page_size: 20,
          search: searchKeyword
        })
        personSearchResults.value = response.data?.data || []
      } catch (error) {
        console.error('搜索纪念人失败:', error)
        personSearchResults.value = []
      } finally {
        personSearchLoading.value = false
      }
    }

    const handlePersonSearch = () => {
      personDropdownOpen.value = true
      if (personSearchTimer) clearTimeout(personSearchTimer)
      personSearchTimer = setTimeout(searchPersons, 300)
    }

    const selectPerson = (person) => {
      selectedPerson.value = person
      personSearchQuery.value = person.name || ''
      personDropdownOpen.value = false
      personSearchResults.value = []
    }

    const clearSelectedPerson = () => {
      selectedPerson.value = null
      personSearchQuery.value = ''
      personSearchResults.value = []
    }

    const closePersonDropdown = () => {
      personDropdownOpen.value = false
    }

    const fillPersonFromId = (personId) => {
      if (personId == null || personId === '') {
        clearSelectedPerson()
        return
      }
      selectedPerson.value = { id: personId, name: `纪念人 #${personId}` }
      personSearchQuery.value = selectedPerson.value.name
    }

    const resetForm = () => {
      form.value = { ...EMPTY_FORM }
      clearSelectedPerson()
    }

    const openCreateModal = () => {
      resetForm()
      modalTitle.value = '新增模板'
      modalVisible.value = true
    }

    const openEditModal = async (row) => {
      const id = row?.id
      if (id == null) {
        alert('缺少模板 ID')
        return
      }

      modalTitle.value = '编辑模板'
      modalVisible.value = true
      detailLoading.value = true
      resetForm()

      try {
        const response = await getCommentTemplateDetail(id)
        const detail = normalizeDetail(response)
        form.value = {
          id: detail.id ?? '',
          content: detail.content || '',
          scope: detail.scope || 'global',
          category: detail.category || '',
          sort_order: detail.sort_order == null ? 0 : Number(detail.sort_order),
          state: detail.state == null ? 1 : Number(detail.state)
        }
        if (form.value.scope === 'person') {
          fillPersonFromId(detail.person_id)
        }
      } catch (error) {
        console.error('获取模板详情失败:', error)
        modalVisible.value = false
        alert(error.message || '获取模板详情失败，请稍后重试')
      } finally {
        detailLoading.value = false
      }
    }

    const closeModal = () => {
      modalVisible.value = false
      detailLoading.value = false
      resetForm()
    }

    const handleSave = async () => {
      const text = String(form.value.content || '').trim()
      if (!text) {
        alert('请填写评论内容')
        return
      }

      const payload = {
        content: text,
        scope: form.value.scope,
        sort_order: Number(form.value.sort_order) || 0,
        state: Number(form.value.state)
      }

      const categoryText = String(form.value.category || '').trim()
      if (categoryText) payload.category = categoryText

      if (form.value.id) payload.id = Number(form.value.id)

      if (form.value.scope === 'person') {
        const personId = selectedPerson.value?.id
        if (personId == null || personId === '') {
          alert('请选择纪念人')
          return
        }
        payload.person_id = Number(personId)
      }

      saving.value = true
      try {
        await saveCommentTemplate(payload)
        alert(form.value.id ? '模板更新成功' : '模板创建成功')
        closeModal()
        await fetchTemplateList()
      } catch (error) {
        console.error('保存评论模板失败:', error)
        alert(error.message || '保存失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handleDelete = async (template) => {
      if (!confirm(`确定删除评论模板「${template.content}」吗？`)) return

      loading.value = true
      try {
        await deleteCommentTemplate(template.id)
        alert('删除成功')
        await fetchTemplateList()
      } catch (error) {
        console.error('删除评论模板失败:', error)
        alert(error.message || '删除失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchTemplateList()
    }

    const handleSearchInput = () => {
      if (listSearchTimer) clearTimeout(listSearchTimer)
      listSearchTimer = setTimeout(() => {
        currentPage.value = 1
        fetchTemplateList()
      }, 300)
    }

    const handleSearch = () => {
      if (listSearchTimer) {
        clearTimeout(listSearchTimer)
        listSearchTimer = null
      }
      currentPage.value = 1
      fetchTemplateList()
    }

    const resetFilters = () => {
      filterScope.value = ''
      filterCategory.value = ''
      filterState.value = '1'
      filterPersonId.value = ''
      keyword.value = ''
      currentPage.value = 1
      fetchTemplateList()
    }

    watch(
      () => form.value.scope,
      (value) => {
        if (value === 'global') {
          clearSelectedPerson()
        }
      }
    )

    onMounted(() => {
      fetchTemplateList()
    })

    return {
      templateList,
      loading,
      saving,
      detailLoading,
      currentPage,
      pageSize,
      totalPages,
      total,
      filterScope,
      filterCategory,
      filterState,
      filterPersonId,
      keyword,
      modalVisible,
      modalTitle,
      form,
      selectedPerson,
      personSearchQuery,
      personSearchResults,
      personSearchLoading,
      personDropdownOpen,
      pickScopeLabel,
      pickStateLabel,
      openCreateModal,
      openEditModal,
      closeModal,
      handlePersonSearch,
      selectPerson,
      clearSelectedPerson,
      closePersonDropdown,
      handleSave,
      handleDelete,
      handlePageChange,
      handleSearchInput,
      handleSearch,
      resetFilters
    }
  }
})
